import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, FormControl, Grid, InputLabel, List, ListItem,
  ListItemText, MenuItem, Select, Snackbar, Stack, Switch, Typography,
} from '@mui/material';
import {
  Check as CheckIcon, Close as CloseIcon, ContentCopy as CopyIcon, Edit as EditIcon, OpenInNew as OpenIcon,
  ReceiptLong as OrdersIcon, RestaurantMenu as RestaurantMenuIcon, ShoppingBag as BagIcon, Visibility as ViewsIcon,
  HourglassTop as PendingIcon, Payments as SoldIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MenuService from '../../services/menu.service';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { apiError, formatPrice } from './menuUtils';

// Local calendar day as Y-m-d (the API reads these as the restaurant's local days).
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const Kpi = ({ icon, label, value, sub, color }) => (
  <Grid size={{ xs: 6, md: 3 }}>
    <Card sx={{ height: '100%', borderRadius: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          <Box minWidth={0}>
            <Typography color="text.secondary" variant="body2">{label}</Typography>
            <Typography variant="h5" fontWeight="bold" color={color} sx={{ fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>{value}</Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
          <Box flexShrink={0} sx={{ display: { xs: 'none', sm: 'block' } }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  </Grid>
);

/**
 * Home dashboard for `menu`-profile tenants (restaurants using the digital
 * menu): today's numbers, the orders waiting to be confirmed, best sellers
 * and quick sold-out toggles. Refreshes the orders every 30s.
 */
const MenuDashboardSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('menu.edit');
  const canManage = hasPermission('menu.manage');
  const canOrders = hasPermission('menu.orders');

  const [menus, setMenus] = useState(null);
  const [selected, setSelected] = useState('');
  const [menu, setMenu] = useState(null);
  const [report, setReport] = useState(null);
  const [pending, setPending] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = useCallback((message, severity = 'success') => setSnackbar({ open: true, message, severity }), []);

  useEffect(() => {
    MenuService.getMenus()
      .then((r) => {
        const list = r.data.data || [];
        setMenus(list);
        if (list.length) setSelected(list[0].uuid);
      })
      .catch((e) => setError(apiError(e, t('menus.dash.errorLoading'))));
  }, [t]);

  useEffect(() => {
    if (!selected) return;
    setMenu(null);
    MenuService.getMenu(selected)
      .then((r) => setMenu(r.data.data))
      .catch((e) => setError(apiError(e, t('menus.dash.errorLoading'))));
  }, [selected, t]);

  const loadOrders = useCallback(async () => {
    if (!selected || !canOrders) return;
    try {
      const day = today();
      const rep = (await MenuService.getOrdersReport(selected, { from: day, to: day })).data.data;
      // Pending orders from earlier days still need an answer: list from the oldest one.
      const list = rep.pending_total
        ? (await MenuService.getOrders(selected, { from: rep.oldest_pending || day, to: day, status: 'pending', per_page: 10 })).data.data
        : [];
      setReport(rep);
      setPending(list);
    } catch (e) {
      notify(apiError(e, t('menus.errorLoadingOrders')), 'error');
    }
  }, [selected, canOrders, notify, t]);

  useEffect(() => {
    loadOrders();
    const id = setInterval(loadOrders, 30000);
    return () => clearInterval(id);
  }, [loadOrders]);

  const act = async (order, action) => {
    if (action === 'cancel' && !window.confirm(t('menus.cancelOrderConfirm', { number: order.number }))) return;
    setBusy(true);
    try {
      const res = action === 'confirm'
        ? await MenuService.confirmOrder(selected, order.uuid)
        : await MenuService.cancelOrder(selected, order.uuid);
      notify(res.data.message);
    } catch (e) {
      notify(apiError(e, t('menus.errorSaving')), 'error');
    } finally {
      setBusy(false);
      loadOrders();
    }
  };

  const toggleAvailable = async (item) => {
    const patch = (available) => setMenu((m) => ({
      ...m,
      sections: m.sections.map((s) => ({ ...s, items: s.items.map((i) => (i.uuid === item.uuid ? { ...i, available } : i)) })),
    }));
    patch(!item.available);
    try {
      await MenuService.updateItem(selected, item.uuid, { available: !item.available });
      notify(item.available ? t('menus.nowSoldOut', { name: item.name }) : t('menus.nowAvailable', { name: item.name }));
    } catch (e) {
      patch(item.available);
      notify(apiError(e, t('menus.errorSaving')), 'error');
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(menu.public_url); notify(t('menus.linkCopied')); } catch { notify(menu.public_url, 'info'); }
  };

  // Visible dishes, sold out first so staff see what is off the menu right now.
  const dishes = useMemo(() => {
    if (!menu) return [];
    return menu.sections
      .filter((s) => s.active)
      .flatMap((s) => s.items.filter((i) => i.active).map((i) => ({ ...i, sectionName: s.name })))
      .sort((a, b) => Number(a.available) - Number(b.available));
  }, [menu]);
  const soldOut = dishes.filter((i) => !i.available).length;

  if (error) return <Alert severity="warning">{error}</Alert>;
  if (!menus) return <Box display="flex" justifyContent="center" p={3}><CircularProgress size={28} /></Box>;

  if (menus.length === 0) {
    return (
      <Card sx={{ borderRadius: 2, textAlign: 'center' }}>
        <CardContent sx={{ py: 6 }}>
          <RestaurantMenuIcon color="primary" sx={{ fontSize: 56, mb: 1 }} />
          <Typography variant="h5" fontWeight="bold" gutterBottom>{t('menus.emptyTitle')}</Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 480, mx: 'auto', mb: 3 }}>{t('menus.emptyHint')}</Typography>
          {canManage && <Button variant="contained" onClick={() => navigate('/menus')}>{t('menus.create')}</Button>}
        </CardContent>
      </Card>
    );
  }

  if (!menu) return <Box display="flex" justifyContent="center" p={3}><CircularProgress size={28} /></Box>;

  const money = (v) => formatPrice(menu.currency_symbol, v);
  const s = report?.summary;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
          {menus.length > 1 ? (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{t('menus.dash.pickMenu')}</InputLabel>
              <Select value={selected} label={t('menus.dash.pickMenu')} onChange={(e) => setSelected(e.target.value)}>
                {menus.map((m) => <MenuItem key={m.uuid} value={m.uuid}>{m.name}</MenuItem>)}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="h4" noWrap>{menu.name}</Typography>
          )}
          <Chip size="small" color={menu.published ? 'success' : 'default'} label={menu.published ? t('menus.published') : t('menus.draft')} />
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {menu.published && <Button size="small" startIcon={<CopyIcon />} onClick={copyLink}>{t('menus.copyLink')}</Button>}
          <Button size="small" startIcon={<OpenIcon />} href={menu.published ? menu.public_url : menu.preview_url} target="_blank" rel="noopener">
            {menu.published ? t('menus.openMenu') : t('menus.openPreview')}
          </Button>
          <Button size="small" variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/menus/${menu.uuid}`)}>{t('menus.dash.editMenu')}</Button>
        </Stack>
      </Box>

      {!menu.published && <Alert severity="info" sx={{ mb: 3 }}>{t('menus.draftHintReadOnly')}</Alert>}

      <Grid container spacing={3}>
        {canOrders && (
          <>
            <Kpi icon={<BagIcon color="primary" sx={{ fontSize: 40 }} />} label={t('menus.dash.ordersToday')} value={s ? s.orders : '—'} />
            <Kpi icon={<PendingIcon color="warning" sx={{ fontSize: 40 }} />} label={t('menus.orderStatus.pending')}
              value={report ? report.pending_total : '—'} color={report?.pending_total ? 'warning.main' : undefined} />
            <Kpi icon={<SoldIcon color="success" sx={{ fontSize: 40 }} />} label={t('menus.dash.soldToday')} value={s ? money(s.confirmed.amount) : '—'}
              sub={s ? t('menus.statSoldHint', { count: s.confirmed.orders, avg: money(s.average_ticket) }) : null} />
          </>
        )}
        <Kpi icon={<ViewsIcon color="action" sx={{ fontSize: 40 }} />} label={t('menus.dash.visits')} value={(menu.view_count || 0).toLocaleString()}
          sub={t('menus.dash.visitsHint')} />

        {canOrders && (
          <Grid size={{ xs: 12, md: 7 }}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6" fontWeight="bold">{t('menus.dash.pendingTitle')}</Typography>
                  <Button size="small" startIcon={<OrdersIcon />} onClick={() => navigate(`/menus/${menu.uuid}?tab=orders`)}>{t('menus.dash.allOrders')}</Button>
                </Box>
                {!menu.whatsapp_orders && <Typography variant="body2" color="text.secondary" mb={1}>{t('menus.ordersOffHint')}</Typography>}
                {pending.length === 0 && <Typography variant="body2" color="text.secondary" py={3} textAlign="center">{t('menus.dash.noPending')}</Typography>}
                <List disablePadding>
                  {pending.map((o, idx) => {
                    const created = new Date(o.created_at);
                    return (
                      <React.Fragment key={o.uuid}>
                        {idx > 0 && <Divider component="li" />}
                        <ListItem disableGutters sx={{ py: 1.5, gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                          <ListItemText
                            sx={{ minWidth: 0 }}
                            primary={(
                              <Box display="flex" gap={1} alignItems="baseline" flexWrap="wrap">
                                <Typography fontWeight={700}>#{o.number}</Typography>
                                {o.customer_name && <Typography noWrap>{o.customer_name}</Typography>}
                                <Typography variant="caption" color="text.secondary">
                                  {created.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} · {created.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                              </Box>
                            )}
                            secondary={o.items.map((i) => `${i.quantity} × ${i.name}${i.option_label ? ` (${i.option_label})` : ''}`).join(', ')}
                            secondaryTypographyProps={{ noWrap: true }}
                          />
                          <Typography fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{money(o.total)}</Typography>
                          <Stack direction="row" spacing={1} flexShrink={0}>
                            <Button size="small" variant="contained" color="success" startIcon={<CheckIcon />} disabled={busy} onClick={() => act(o, 'confirm')}>
                              {t('menus.confirmOrder')}
                            </Button>
                            <Button size="small" color="error" startIcon={<CloseIcon />} disabled={busy} onClick={() => act(o, 'cancel')}>
                              {t('menus.cancelOrder')}
                            </Button>
                          </Stack>
                        </ListItem>
                      </React.Fragment>
                    );
                  })}
                </List>
                {report?.pending_total > pending.length && (
                  <Typography variant="caption" color="text.secondary">{t('menus.dash.morePending', { count: report.pending_total - pending.length })}</Typography>
                )}

                <Typography variant="h6" fontWeight="bold" mt={3} mb={1}>{t('menus.dash.topToday')}</Typography>
                {!report?.products?.length && <Typography variant="body2" color="text.secondary">{t('menus.dash.noSalesToday')}</Typography>}
                <List dense disablePadding>
                  {(report?.products || []).slice(0, 5).map((p) => (
                    <ListItem key={`${p.name}|${p.option_label || ''}`} disableGutters secondaryAction={<Typography fontWeight={600}>{money(p.amount)}</Typography>} sx={{ pr: 10 }}>
                      <ListItemText primary={`${p.quantity} × ${p.name}${p.option_label ? ` · ${p.option_label}` : ''}`} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid size={{ xs: 12, md: canOrders ? 5 : 12 }}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5} gap={1}>
                <Typography variant="h6" fontWeight="bold">{t('menus.dash.availabilityTitle')}</Typography>
                {soldOut > 0 && <Chip size="small" color="error" variant="outlined" label={t('menus.dash.soldOutCount', { count: soldOut })} />}
              </Box>
              <Typography variant="body2" color="text.secondary" mb={1}>{t('menus.availableHint')}</Typography>
              {dishes.length === 0 && <Typography variant="body2" color="text.secondary" py={3} textAlign="center">{t('menus.dash.noDishes')}</Typography>}
              <List dense disablePadding sx={{ maxHeight: 420, overflowY: 'auto' }}>
                {dishes.map((i) => (
                  <ListItem key={i.uuid} disableGutters secondaryAction={(
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="caption" fontWeight={600} color={i.available ? 'success.main' : 'error.main'}>
                        {i.available ? t('menus.availableShort') : t('menus.soldOut')}
                      </Typography>
                      <Switch size="small" checked={!!i.available} disabled={!canEdit} onChange={() => toggleAvailable(i)} inputProps={{ 'aria-label': t('menus.available') }} />
                    </Box>
                  )} sx={{ pr: 15 }}>
                    <ListItemText primary={i.name} secondary={i.sectionName} primaryTypographyProps={{ noWrap: true }} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default MenuDashboardSection;
